import { useUpdateEffect } from '@cian/react-utils';

import { useMemo, useRef } from 'react';

import { useHoveredOfferContext } from '@/app/shared/context/HoveredOfferContext';

import { useMapFeaturesContext } from '@/microfrontends/map/shared/context/MapLayersCenterContext';
import { useMapStoreContext } from '@/microfrontends/map/shared/context/mapStoreContext';
import { PinService } from '@/microfrontends/map/shared/services/PinService';
import { convertClusterToRBushItem } from '@/microfrontends/map/shared/utils/map/convertClusterToRBushItem';
import { getTilesToUpdate } from '@/microfrontends/map/shared/utils/map/tiles';

import { useGetClustersQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/query';
import { clustersSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/selectors';
import { useMapConfigurationSelector } from '@/packages/redux-state/store/slices/mapConfiguration';

import pointerIcon from '../../assets/pointer.svg';

/**
 * @description Хук для отображения указателя на карте при наведении на объявление в списке
 */
export const useHoveredOfferPointer = (): void => {
  const { rbush, pinsLayer, balloonManager } = useMapFeaturesContext();

  const { ymaps, map } = useMapStoreContext();

  const { bounds, zoom } = useMapConfigurationSelector();

  const { hoveredOfferGeoHash } = useHoveredOfferContext();

  const { data: clusters, isFetching, isSuccess } = useGetClustersQuery({ select: clustersSelector });

  const clusterByHoveredOffer = useMemo(
    () => clusters?.find(({ id }) => hoveredOfferGeoHash?.includes(id)),
    [clusters, hoveredOfferGeoHash],
  );

  const pointerObjectRef = useRef<YMaps.IGeoObject | null>(null);

  useUpdateEffect(() => {
    if (!clusterByHoveredOffer || isFetching || !isSuccess) {
      return () => {};
    }

    /** Находим rbushItem по кластеру */
    const rbushItem = convertClusterToRBushItem(clusterByHoveredOffer);

    /** Вырезаем найденный item из дерева */
    rbush.remove(rbushItem, (itemA, itemB) => itemA.cluster.id === itemB.cluster.id);

    /** Если есть balloon с ценой, то также вырезаем */
    const hasBalloon = new PinService({ cluster: clusterByHoveredOffer }).provideIsWithBalloon();
    if (hasBalloon) {
      balloonManager.removeBalloon(clusterByHoveredOffer.id);
    }

    /** Находим тайл, где есть вырезанный элемент и обновляем пины в этом тайле */
    const tilesToUpdate = getTilesToUpdate({ bounds, includeShadow: true, rbushItems: [rbushItem], zoom });
    pinsLayer.layer.update(tilesToUpdate);

    return () => {
      const foundRbushItems = rbush.search(rbushItem);

      /** Если элемент уже есть в дереве, то ничего не делаем */
      if (foundRbushItems.length) {
        return;
      }

      /** При снятии ховера добавляем элемент обратно в дерево */
      rbush.insert(rbushItem);

      /** Также возвращаем balloon с ценой, если он был */
      if (hasBalloon) {
        balloonManager.updateBalloon(clusterByHoveredOffer);
      }

      /** Обновляем тайл, чтобы вернуть пин на карту */
      pinsLayer.layer.update(tilesToUpdate);
    };
  }, [clusterByHoveredOffer, isFetching, isSuccess]);

  useUpdateEffect(() => {
    /** Создаем объект с иконкой указателя */
    pointerObjectRef.current = new ymaps.GeoObject(clusterByHoveredOffer, {
      iconLayout: ymaps.templateLayoutFactory.createClass(`<img src="${pointerIcon}" />`),
      iconOffset: [-24, -70],
    });

    /** Добавляем на карту */
    map.geoObjects.add(pointerObjectRef.current);

    return () => {
      if (pointerObjectRef.current) {
        map.geoObjects.remove(pointerObjectRef.current);

        pointerObjectRef.current = null;
      }
    };
  }, [clusterByHoveredOffer, clusters]);
};
